import { useState } from "react";
import { Box, Divider, Stack, Typography, Button } from "@mui/material";
import { AnimatePresence, motion, HTMLMotionProps } from "motion/react";
import { useTranslation } from "next-i18next";
import { IErrorResponse } from "src/api/client/types";
import { useAddWatchlistWikifoliosMutation, useRemoveWatchlistWikifoliosMutation } from "src/api/client/watchlist.api";
import { IconInfoOuterCircle } from "src/components/icons-mui/info-outer-circle";
import { OverlayLoadingSpinner } from "src/components-mui/base/overlay-loading-spinner/overlay-loading-spinner";
import { PopoverButtonIconInfo } from "src/components-mui/base/wf-popover/popover-button-icon-info";
import { WfPopover } from "src/components-mui/base/wf-popover/wf-popover";
import { WfSlider } from "src/components-mui/base/wf-slider/wf-slider";
import { getGlobals } from "src/consts/globals";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useWfToast, EnumToastStatus } from "src/hooks/use-wf-toast";
import { useUserStore } from "src/stores/user-store";
import { useWikifolioStore } from "src/stores/wikifolio-store";
import { CBDetailsAccordion } from "../components/details-accordion";
import { CBFooter } from "../components/footer";
import { CBHeaderNotInvestible } from "../components/header/header-not-investible";
import { InfoBox } from "../components/info-box";
import { CBPrices } from "../components/prices";
import { CBProgressBar } from "../components/progress-bar";
import { CBRatioTabBar } from "../components/ratio-tab-bar";
import { SpacedContainer } from "../components/spaced-container";
import { CBCertificateFee, CBPerformanceFee } from "../components/statistics";
import { useCertificateBoxContext } from "../context";

const INVESTMENT_MIN = 100;
const INVESTMENT_MAX = 5000;
const INVESTMENT_STEP = 100;

const PREBOOK_MOTION_BOX_NOTWATCHLISTED_TEST_ID = "prebook-motion-box-notwatchlisted";
const PREBOOK_MOTION_BOX_WATCHLISTED_TEST_ID = "prebook-motion-box-watchlisted";

const PREBOOK_BUTTON_TEST_ID = "prebook-button";

const MotionBox = motion.div;

const PrebookMotionBox = (props: HTMLMotionProps<"div">) => (
    <MotionBox
        style={{
            position: "relative",
            overflow: "hidden",
            marginLeft: "-16px",
            marginRight: "-16px",
            paddingLeft: "16px",
            paddingRight: "16px",
        }}
        variants={{
            open: {
                height: "auto",
                opacity: 1,
            },
            closed: {
                height: "0px",
                opacity: 0,
            },
        }}
        initial="closed"
        animate="open"
        exit="closed"
        transition={{ bounce: 0, duration: 0.4 }}
        {...props}
    />
);

export const CertificateBoxPublished = () => {
    const { t } = useTranslation("common");
    const { openLoginModal } = useLinkBuilder();
    const toast = useWfToast();
    const { isCurrentUser, isLoggedIn } = useUserStore();
    const {
        dict,
        data: { trader, simplifiedExchangeRate, wikifolio, certificates, tooltips },
        selectedCertificate: certificate,
    } = useCertificateBoxContext();

    const { watchlist, setOnWatchlist } = useWikifolioStore();
    const isOnWatchlist = watchlist[wikifolio.id] ?? false;

    const { formatNumber } = useNumberFormatter();

    const prebookValues = {
        min: INVESTMENT_MIN * simplifiedExchangeRate,
        max: INVESTMENT_MAX * simplifiedExchangeRate,
        step: INVESTMENT_STEP * simplifiedExchangeRate,
    };

    const [buyingInterests, setBuyingInterests] = useState(certificate.buyingInterests!.currentValue);
    const [reservedCapital, setReservedCapital] = useState(certificate.reservedCapital!.currentValue);
    const [intendedInvestment, setIntendedInvestment] = useState((wikifolio.intendedInvestment || INVESTMENT_MIN) * simplifiedExchangeRate);
    const { mutatePromise: addWatchlist } = useAddWatchlistWikifoliosMutation(wikifolio.id);
    const { mutatePromise: removeWatchlist } = useRemoveWatchlistWikifoliosMutation(wikifolio.id);
    const [isLoading, setLoading] = useState(false);

    const alwaysShowOperationalMessage = certificate.hasBlockedReason || !trader.isLegitimized;
    const showOperationalMessage = dict.operationalMessage && (alwaysShowOperationalMessage || !isOnWatchlist);

    const onPrebook = async () => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }

        try {
            setLoading(true);
            await addWatchlist({ intendedInvestment: intendedInvestment / simplifiedExchangeRate });
            setReservedCapital(reservedCapital + intendedInvestment);
            setBuyingInterests(buyingInterests + 1);
            setOnWatchlist(wikifolio.id, true);
            toast({ status: EnumToastStatus.Success, title: t("add-wikifolio-success") });
        } catch (result) {
            setLoading(false);
            toast({ status: EnumToastStatus.Error, title: (result as IErrorResponse)?.message || t("general-error") });
        }
    };

    const onUndoPrebook = async () => {
        try {
            setLoading(true);
            await removeWatchlist();
            setReservedCapital(reservedCapital - intendedInvestment);
            setBuyingInterests(buyingInterests - 1);
            setOnWatchlist(wikifolio.id, false);
            toast({ status: EnumToastStatus.Success, title: t("remove-wikifolio-success") });
        } catch (result) {
            setLoading(false);
            toast({ status: EnumToastStatus.Error, title: (result as IErrorResponse)?.message || t("general-error") });
        }
    };

    return (
        <>
            <CBHeaderNotInvestible tooltip={tooltips.previewMode} />
            <SpacedContainer sx={{ pb: 0 }}>
                {certificates.length > 1 && <CBRatioTabBar />}
                <CBPrices />
                <Divider />

                <CBCertificateFee />
                <CBPerformanceFee />
                <Divider />
            </SpacedContainer>

            <CBDetailsAccordion buttonContainerSx={{ pr: 2 }}>
                <Typography px={2} pb={2} fontSize="xs">
                    {dict.publishedCriteriaParagraph}
                </Typography>
                <Stack spacing={0.5}>
                    <CBProgressBar {...certificate.testPhaseDays!} description={dict.publishedTestPhase} />
                    <CBProgressBar
                        currentValue={buyingInterests}
                        requiredValue={certificate.buyingInterests!.requiredValue}
                        description={dict.publishedBuyingInterests}
                    />
                    <CBProgressBar
                        prefix={getGlobals().currency}
                        currentValue={reservedCapital}
                        requiredValue={certificate.reservedCapital!.requiredValue}
                        description={dict.publishedReservedCapital}
                    />
                </Stack>
                {showOperationalMessage && !isCurrentUser(trader) && (
                    <Box px={2} pt={2}>
                        <InfoBox icon={<IconInfoOuterCircle sx={{ mt: 0.2, width: "18px", height: "18px" }} />}>
                            <Typography fontSize="sm">{dict.operationalMessage}</Typography>
                        </InfoBox>
                    </Box>
                )}
            </CBDetailsAccordion>

            <SpacedContainer>
                {!isCurrentUser(trader) && (
                    <AnimatePresence mode="wait" onExitComplete={() => setLoading(false)}>
                        {!isOnWatchlist && (
                            <PrebookMotionBox key="notwatchlisted" data-test-id={PREBOOK_MOTION_BOX_NOTWATCHLISTED_TEST_ID}>
                                <Stack spacing={3}>
                                    <Stack spacing={1} pt={1}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography fontWeight="bold" fontSize="sm">
                                                {dict.prebookTitle}
                                            </Typography>

                                            <WfPopover
                                                content={tooltips.emissionRequired!.content}
                                                link={tooltips.emissionRequired!.link}
                                                PopoverTrigger={<PopoverButtonIconInfo ariaLabel={dict.prebookTitle} sx={{ ml: 0.5 }} />}
                                            />
                                        </Box>

                                        <Typography fontSize="xs">{dict.prebookParagraph}</Typography>
                                    </Stack>

                                    <Stack spacing={2}>
                                        <Typography>
                                            {getGlobals().currency} <strong>{formatNumber(intendedInvestment)}</strong>
                                        </Typography>

                                        <WfSlider size="small" {...prebookValues} value={intendedInvestment} onChange={setIntendedInvestment} />

                                        <Button
                                            fullWidth
                                            color="green"
                                            size="medium"
                                            variant="contained"
                                            onClick={onPrebook}
                                            data-test-id={PREBOOK_BUTTON_TEST_ID}
                                        >
                                            {dict.prebookButton}
                                        </Button>
                                    </Stack>
                                </Stack>

                                {isLoading && <OverlayLoadingSpinner />}
                            </PrebookMotionBox>
                        )}

                        {isOnWatchlist && (
                            <PrebookMotionBox key="watchlisted" data-test-id={PREBOOK_MOTION_BOX_WATCHLISTED_TEST_ID}>
                                <Stack spacing={2}>
                                    <Stack py={1} spacing={1}>
                                        <Typography fontSize="sm" fontWeight="bold">
                                            {dict.prebookDoneTitle}
                                        </Typography>
                                        <Typography fontSize="xs">{dict.prebookDoneParagraph}</Typography>
                                    </Stack>
                                    <Button fullWidth size="medium" variant="outlined" onClick={onUndoPrebook} data-test-id={PREBOOK_BUTTON_TEST_ID}>
                                        {dict.prebookDoneButton}
                                    </Button>
                                </Stack>
                                {isLoading && <OverlayLoadingSpinner />}
                            </PrebookMotionBox>
                        )}
                    </AnimatePresence>
                )}

                <CBFooter />
            </SpacedContainer>
        </>
    );
};
